<template>
  <div class="mb-16">
    <CheckoutHeader :current-step="2" />
    <CheckoutLayout
      :title="$t('checkout.information')"
      next-link="/checkout/payment"
      :has-gift-card="giftCardShown"
      v-if="cartStore.items.length > 0"
    >
      <div class="flex flex-col gap-4">
        <div v-if="checkoutStore.startPurchaseData.require_email">
          <InputText
            name="customer-email"
            id="customer-email"
            type="email"
            :required="checkoutStore.startPurchaseData.require_email"
            :placeholder="$t('checkout.email_placeholder')"
            :rules="rules.email"
            v-model="cartStore.currentOrderData.email"
          />
          <div class="text-sm mt-2">
            {{ $t('checkout.order_confirmation_will_be_sent_to') }}
          </div>
        </div>
        <InputText
          v-if="checkoutStore.startPurchaseData.require_email"
          name="customer-email2"
          id="customer-email2"
          type="email"
          :required="checkoutStore.startPurchaseData.require_email"
          :placeholder="$t('checkout.repeat_email_placeholder')"
          :rules="rules.emailRepeat"
          v-model="cartStore.currentOrderData.email2"
        />

        <InputText
          v-if="checkoutStore.startPurchaseData.require_name"
          name="customer-name"
          id="customer-name"
          type="text"
          required
          :placeholder="$t('checkout.name_placeholder')"
          :rules="rules.name"
          v-model="cartStore.currentOrderData.name"
        />

        <InputText
          v-if="checkoutStore.startPurchaseData.require_address"
          name="customer-address"
          id="customer-address"
          type="text"
          :required="checkoutStore.startPurchaseData.require_address"
          :placeholder="$t('checkout.address_placeholder')"
          :rules="rules.address"
          v-model="cartStore.currentOrderData.address"
        />

        <div class="flex gap-4" v-if="checkoutStore.startPurchaseData.require_city_zip">
          <InputText
            id="customer-zip"
            class="w-32"
            name="customer-zip"
            type="text"
            :required="checkoutStore.startPurchaseData.require_city_zip"
            :placeholder="$t('checkout.zip_placeholder')"
            :rules="rules.zip"
            v-model="cartStore.currentOrderData.zip"
          />
          <InputText
            id="customer-city"
            class="flex-1"
            name="customer-city"
            type="text"
            :required="checkoutStore.startPurchaseData.require_city_zip"
            :placeholder="$t('checkout.city_placeholder')"
            :rules="rules.city"
            v-model="cartStore.currentOrderData.city"
          />
        </div>

        <InputText
          v-if="checkoutStore.startPurchaseData.require_phone"
          name="customer-telephone"
          id="customer-telephone"
          type="tel"
          :required="checkoutStore.startPurchaseData.require_phone"
          :placeholder="$t('checkout.phone_placeholder')"
          :rules="rules.phone"
          v-model="cartStore.currentOrderData.telephone"
        />

        <InputSelect
          v-if="checkoutStore.startPurchaseData.require_country"
          id="customer-country"
          name="customer-country"
          :required="checkoutStore.startPurchaseData.require_country"
          :options="countryOptions"
          v-model="cartStore.currentOrderData.country"
          :placeholder="$t('checkout.country_placeholder')"
          :rules="rules.country"
          :hide-title="false"
        />

        <div v-if="cartStore.currentOrderData.customfields_data && cartStore.currentOrderData.customfields_data['per_event']">

          <CheckoutCustomFields
            type="ticket"
            :event-id="undefined"
            :ticket-id="undefined"
            :filters="['per_event']"
            :errors="undefined"
          />
        </div>

        <div >
          <div v-if="checkoutStore.customFieldErrors.length > 0">
            <div v-for="(eventItem, eventIndex) in cartStore.items" :key="eventIndex">
              <div v-for="(ticketItem, ticketIndex) in cartStore.items[eventIndex].tickets" :key="ticketIndex">
                <div v-if="cartStore.currentOrderData.customfields_data &&
                      cartStore.items[eventIndex].id &&
                      cartStore.currentOrderData.customfields_data[cartStore.items[eventIndex].id] &&
                      checkoutStore.customFieldErrors.length > 0">
                  <BaseSlideTransition>
                    <CheckoutCustomFields
                      type="ticket"
                      :ticket-id="cartStore.items[eventIndex].tickets[ticketIndex].id"
                      :event-id="cartStore.items[eventIndex].id"
                      :filters="['per_ticket','per_tickettype']"
                      :errors="checkoutStore.customFieldErrors"
                    />
                  </BaseSlideTransition>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-4 text-red-600" v-if="checkoutStore.orderErrors && checkoutStore.orderErrors.length > 0">
            <ul>
              <li v-for="error in checkoutStore.orderErrors" :key="error">
                {{ error }}
              </li>
            </ul>
          </div>
        </div>

        <div v-if="disabledContent"
          class="flex items-start mt-8 text-text gap-2 bg-surface-grey-light p-4 rounded-sm text-sm"
        >
          <TicketIcon class="w-6 h-6 fill-primary inline-block shrink-0" />
          <div class="mt-[2px]">
            {{ $t('checkout.tickets_recievement_notice') }}
          </div>
        </div>

        <InputCheckbox
          class="mt-4"
          id="customer-terms"
          name="customer-terms"
          v-model="termsAccepted"
          :required="true"
          :rules="rules.terms"
        >
          {{ $t('checkout.terms_label_prefix') }}<button
            type="button"
            class="underline hover:opacity-70 transition-opacity"
            @click.prevent="termsModalOpen = true"
          >{{ $t('checkout.terms_label_link') }}</button>
        </InputCheckbox>

        <BaseModal v-model="termsModalOpen" size="lg">
          <template #header>{{ $t('checkout.terms_label_header') }}</template>
          <div class="p-6">
            <CheckoutTermsContent />
          </div>
        </BaseModal>

        <InputCheckbox
          v-if="checkoutStore.startPurchaseData.has_newsletter"
          class="mt-4"
          id="customer-newsletter"
          name="customer-newsletter"
          :label="
            (checkoutStore.startPurchaseData.custom_newsletter && checkoutStore.startPurchaseData.custom_newsletter.length > 0) 
              ? checkoutStore.startPurchaseData.custom_newsletter 
              : $t('checkout.newsletter_label', { organizer: generalStore.general 
                ? generalStore.general.customer.name 
                : '' 
              })
            "
          :rules="undefined"
          v-model="cartStore.currentOrderData.newsletter"
        />
      </div>
    </CheckoutLayout>
  </div>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { computed,ref,watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';
  import * as yup from 'yup';

  import CheckoutCustomFields from '@/components/checkout/CheckoutCustomFields.vue';
  import CheckoutHeader from '@/components/checkout/CheckoutHeader.vue';
  import CheckoutLayout from '@/components/checkout/CheckoutLayout.vue';
  import CheckoutTermsContent from '@/components/checkout/CheckoutTermsContent.vue';
  import TicketIcon from '@/components/icons/TicketIcon.vue';
  import BaseModal from '@/components/ui/BaseModal.vue';
  import BaseSlideTransition from '@/components/ui/BaseSlideTransition.vue';
  import InputCheckbox from '@/components/ui/inputs/InputCheckbox.vue';
  import InputSelect from '@/components/ui/inputs/InputSelect.vue';
  import InputText from '@/components/ui/inputs/InputText.vue';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { useGeneralStore } from '@/stores/general';
  import { getCityFromZip } from '@/utils/getCityFromZip';
  import { setNonRequiredFields } from '@/utils/setNonRequiredFields';
  import { useWebsocketStore } from '@/websocket/websocket';

  const { t } = useI18n();
  const { locale } = useI18n();
    const router = useRouter();
  const termsAccepted = ref(false);
  const termsModalOpen = ref(false);
  const disabledContent = false; // Instead of deleting the hmtl we just hide it


  useHead({
    title: t('checkout.information'),
    meta: [
      {
        property: 'og:title',
        content: t('checkout.information'),
      },
    ],
  });
  const cartStore = useCartStore();
  const websocketStore = useWebsocketStore();
  const checkoutStore = useCheckoutStore();
  const generalStore = useGeneralStore();

  websocketStore.send('startpurchase', { language: locale.value }, undefined);
  checkoutStore.orderErrors = []; // reset order errors when entering information step

  // If startPurchaseData.vouchercode_data is ever not empty we need to set its corresponding input field values here as well, but only once
  watch(() => checkoutStore.startPurchaseData.vouchercode_data, (newVal) => {
    if(!checkoutStore.vouchercodeDataSet && newVal && Object.keys(newVal).length > 0) {
      for (const [key, value] of Object.entries(newVal)) {

        // `key in object` narrows intent at runtime; casting to Record<string, unknown> satisfies
        // TypeScript since currentOrderData has no index signature, but the `in` check ensures safety
        if (key in cartStore.currentOrderData) {
          (cartStore.currentOrderData as Record<string, unknown>)[key] = value;
        }

        if (key === "email") {
          cartStore.currentOrderData.email2 = value;
        }
      }
      checkoutStore.vouchercodeDataSet = true;
    }
  }, { immediate: true });

  // We want to automatically set Danish cities based on the Danish zip code. Only Danish zip codes are implemented so far, all of them are 3-4 digits long.
  watch(() => cartStore.currentOrderData.zip, (newZip) => {
    if(newZip && (newZip.length == 3 || newZip.length == 4)) {
      const city = getCityFromZip(Number(newZip));
      if(city) {
        cartStore.currentOrderData.city = city;
      }
    }
  });

  //If input fields are not required then set currentOrderData values to '-' to avoid issues with websocket
  setNonRequiredFields();

  //if there is nothing in the cart we need to route back to the frontpage, since there is no order to checkout, this needs to be a timeout to avoid routing before the cart is populated from localstorage on page load
  setTimeout(() => {
    if(cartStore.items.length == 0) {
      if(cartStore.paymentFailedAvailable){
        cartStore.paymentFailedAvailable = false;
        router.push('/checkout/payment');
      }
      else {
        router.push('/');
      }
    }
  }, 2000);

  // Computed const from startPurchaseData.countries where value is the same as label
  const countryOptions = computed(() => {
    return checkoutStore.startPurchaseData.countries.map((country: string) => ({
      value: country,
      label: country,
    }));
  });

  
  // If currentOrderData.country is empty, we want to set it to the first country in the list. Websocket handles which are relevant.
  // We also want to set the country field's selected value to match currentOrderData.country when this happens
  watch(countryOptions, (newOptions) => {
    if(newOptions.length > 0 && (!cartStore.currentOrderData.country || cartStore.currentOrderData.country == '')) {
      cartStore.currentOrderData.country = newOptions[0].value;
    }
  }, { immediate: true });

  //disabled with false for now, awaiting implementation
  const giftCardShown = computed(() => {
    return (
      false &&
      cartStore.items.some(item => item.giftcard_allowed)
    );
  });

  const rules = {
    email: yup
      .string()
      .email(t('validation.email'))
      .required(t('validation.required')),

    emailRepeat: yup
      .string()
      .email(t('validation.email'))
      .required(t('validation.required'))
      .test(
        'emails-match',
        t('validation.emails_must_match'),
        value => value === cartStore.currentOrderData.email
      ),

    name: yup.string().required(t('validation.required')),
    address: yup.string().required(t('validation.required')),
    zip: yup
      .string()
      .matches(/^\d{1,25}$/, t('validation.zip'))
      .required(t('validation.required')),
    city: yup.string().required(t('validation.required')),
    phone: yup
      .string()
      .matches(/^[\d +()-]{7,}$/, t('validation.phone'))
      .required(t('validation.required')),
    country: yup.string().required(t('validation.required')),
    terms: yup.boolean().isTrue(t('validation.terms')).required(t('validation.terms')),
  };
</script>
